import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar
} from '@mui/material';
import {
  Timer as TimerIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useQuizScoring } from '../../../hooks/useQuizScoring';

const QuizRules = ({ config, quizData, onStart, onBack, isLiveMode = false }) => {
  const { getScoringRules } = useQuizScoring(quizData?.difficulty);

  const scoringRules = getScoringRules();

  const formatScore = (score) => {
    if (score > 0) return `+${score.toLocaleString()}`;
    if (score < 0) return score.toLocaleString();
    return '0';
  };

  return (
    <Card sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Quiz Rules & Scoring
        </Typography>

        {/* Quiz Overview */}
        <Box sx={{ mb: 4, p: 3, bgcolor: 'primary.light', borderRadius: 2, color: 'primary.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            Your Quiz Configuration
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2">Difficulty</Typography>
              <Typography variant="h6">
                {config.difficulty_id === 'all' ? 'All Difficulties' : quizData?.difficulty?.level_name}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2">Duration</Typography>
              <Typography variant="h6">{config.duration_minutes} min</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2">Total Songs</Typography>
              <Typography variant="h6">{quizData?.quiz_config?.total_songs}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2">Total Questions</Typography>
              <Typography variant="h6">{quizData?.quiz_config?.total_questions}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={4}>
          {/* Timer Rules */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <TimerIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Timer Rules</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 24, height: 24 }}>
                        <Typography variant="caption">M</Typography>
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Main Timer"
                      secondary={`${config.duration_minutes} minutes total`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'secondary.main', width: 24, height: 24 }}>
                        <Typography variant="caption">S</Typography>
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Song Timer"
                      secondary="1 minute per song"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'warning.main', width: 24, height: 24 }}>
                        <Typography variant="caption">Q</Typography>
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Question Timer"
                      secondary={
                        config.difficulty_id === 'all'
                          ? "Varies by difficulty (10-30s)"
                          : `${scoringRules?.baseTime || 10} seconds per question`
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Scoring Rules */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <SpeedIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Scoring System</Typography>
                </Box>
                {/* Base Points */}
                {config.difficulty_id !== 'all' && (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Base Points: {scoringRules?.basePoints}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Base Time: {scoringRules?.baseTime}s per question
                    </Typography>
                    {quizData?.difficulty && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Difficulty: {quizData.difficulty.level_name}
                      </Typography>
                    )}
                  </Box>
                )}
                {config.difficulty_id === 'all' ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Scoring varies by difficulty level:
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Fast Correct"
                          secondary="Bonus points for quick answers"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="On-time Correct"
                          secondary="Standard points for correct answers"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Late but Correct Answer"
                          secondary="Reduced points for slow answers"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Wrong Answer"
                          secondary="negative points for quick incorrect answers"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Penalty"
                          secondary="Heavy penalty for incorrect answers"
                        />
                      </ListItem>
                    </List>
                  </Box>
                ) : (
                  scoringRules && (
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Fast Correct (≤${scoringRules.fastThreshold}s)`}
                          secondary={
                            <Chip
                              label={formatScore(scoringRules.scoring.fastCorrect)}
                              color="success"
                              size="small"
                            />
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`On-time Correct (≤${scoringRules.baseTime}s)`}
                          secondary={
                            <Chip
                              label={formatScore(scoringRules.scoring.onTimeCorrect)}
                              color="primary"
                              size="small"
                            />
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Late Correct"
                          secondary={
                            <Chip
                              label={formatScore(scoringRules.scoring.lateCorrect)}
                              color="warning"
                              size="small"
                            />
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Wrong Answer"
                          secondary={
                            <Chip
                              label={formatScore(scoringRules.scoring.wrongLate)}
                              color="error"
                              size="small"
                            />
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CancelIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Penalty"
                          secondary={
                            <Chip
                              label={formatScore(scoringRules.scoring.wrongPenalty)}
                              color="error"
                              size="small"
                            />
                          }
                        />
                      </ListItem>
                    </List>
                  )
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Game Rules */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            How to Play
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" paragraph>
                • Answer questions about songs as quickly and accurately as possible
              </Typography>
              <Typography variant="body2" paragraph>
                • Faster correct answers earn bonus points
              </Typography>
              <Typography variant="body2" paragraph>
                • Wrong answers may result in point penalties
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" paragraph>
                • If you run out of time on a question, it's marked as incorrect
              </Typography>
              <Typography variant="body2" paragraph>
                • When the song timer expires, remaining questions are skipped
              </Typography>
              <Typography variant="body2" paragraph>
                • The quiz ends when the main timer reaches zero
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {!isLiveMode && onBack && (
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={onBack}
            >
              Back to Config
            </Button>
          )}

          <Button
            variant="contained"
            size="large"
            startIcon={<ArrowForwardIcon />}
            onClick={onStart}
            sx={{ minWidth: 200, ml: isLiveMode ? 0 : 'auto' }}
          >
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default QuizRules;
